/**
 * <pd-event-register-form>
 *
 * Single shared modal per Events Block Instance. Tile buttons with
 *   data-pd-event-register-trigger data-block-id="..." data-event-*="..."
 * dispatch click events that this element handles via document-level delegation,
 * scoped by matching data-block-id.
 *
 * On submit, fires two parallel Klaviyo Client API calls:
 *   POST /client/events/?company_id=...   — metric "Registered for Event"
 *   POST /client/subscriptions/?company_id=...   — Events Interest list
 *
 * See:
 *   docs/adr/0001-klaviyo-metric-triggered-flow.md   (why metric-trigger, not list-trigger)
 *   docs/adr/0002-render-pd-snippet-from-ai-gen-block.md
 *   docs/adr/0003-multi-store-klaviyo-via-metafields.md
 *
 * Depends on DetailsModal (src/assets/details-modal.js) being loaded first.
 * Depends on trapFocus/removeTrapFocus globals from src/assets/global.js.
 */
if (!customElements.get('pd-event-register-form')) {
  customElements.define(
    'pd-event-register-form',
    class PdEventRegisterForm extends DetailsModal {
      constructor() {
        super();

        this.blockId = this.dataset.blockId || '';
        this.companyId = this.dataset.klaviyoCompanyId || '';
        this.listId = this.dataset.klaviyoListId || '';
        this.klaviyoRevision = '2024-10-15';
        this.klaviyoBaseUrl = 'https://a.klaviyo.com/client';
        this.metricName = 'Registered for Event';

        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('.pd-event-register-form__submit');
        this.submitSpinner = this.querySelector('.pd-event-register-form__submit-spinner');
        this.errorWrapper = this.querySelector('.pd-event-register-form__error');
        this.errorMessage = this.querySelector('[data-pd-event-register-form-error-message]');

        this.submissionTimestamps = [];
        this.maxSubmissionsPer60s = 3;
        this.sessionStorageKey = 'pd-event-registered';

        if (!this.companyId || !this.listId) {
          console.warn(
            '[pd-event-register-form] Missing klaviyo metafields. ' +
              'Set shop.metafields.custom.klaviyo_company_id and ' +
              'shop.metafields.custom.klaviyo_events_interest_list_id. ' +
              'Form will reject submissions with a generic error until fixed.'
          );
        }

        this.boundOnTriggerClick = this.onTriggerClick.bind(this);
        document.addEventListener('click', this.boundOnTriggerClick);

        if (this.form) {
          this.form.addEventListener('submit', this.onSubmit.bind(this));
        }

        this.querySelectorAll('[data-pd-event-register-form-close]').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            this.close();
          });
        });

        this.querySelectorAll('.pd-event-register-form__overlay').forEach((overlay) => {
          overlay.addEventListener('click', () => this.close());
        });
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.boundOnTriggerClick);
      }

      onTriggerClick(event) {
        const trigger = event.target.closest('[data-pd-event-register-trigger]');
        if (!trigger) return;
        if (trigger.dataset.blockId !== this.blockId) return;
        if (trigger.hasAttribute('disabled') || trigger.getAttribute('aria-disabled') === 'true') return;

        event.preventDefault();
        this.openWithContext(trigger.dataset);
      }

      openWithContext(triggerData) {
        const eventId = triggerData.eventId || '';
        this.populateContextEverywhere(triggerData);

        if (this.sessionAlreadyRegistered(eventId)) {
          this.setState('success');
        } else {
          this.resetFormForNewSubmission();
          this.populateHiddenFields(triggerData);
          this.setState('idle');
          this.hideError();
        }

        this.open();
      }

      open() {
        if (!this.detailsContainer) return;
        this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
        this.detailsContainer.setAttribute('open', true);
        document.body.addEventListener('click', this.onBodyClickEvent);
        document.body.classList.add('overflow-hidden');

        const focusTarget =
          this.detailsContainer.querySelector('[data-state="idle"] input[type="email"]') ||
          this.detailsContainer.querySelector('input:not([type="hidden"])');
        trapFocus(this.detailsContainer.querySelector('[tabindex="-1"]') || this.detailsContainer, focusTarget);
      }

      close(focusToggle = true) {
        if (!this.detailsContainer) return;
        removeTrapFocus(focusToggle ? this.summaryToggle : null);
        this.detailsContainer.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClickEvent);
        document.body.classList.remove('overflow-hidden');
      }

      onBodyClick(event) {
        if (!this.contains(event.target)) return;
        if (event.target.classList.contains('modal-overlay')) this.close(false);
      }

      onSummaryClick(event) {
        event.preventDefault();
      }

      resetFormForNewSubmission() {
        if (!this.form) return;
        this.form
          .querySelectorAll('input[type="checkbox"], input[type="email"], input[type="text"]')
          .forEach((input) => {
            if (input.name === 'website') return;
            if (input.type === 'checkbox') input.checked = false;
            else input.value = '';
          });
      }

      populateHiddenFields(triggerData) {
        const map = {
          event_id: triggerData.eventId,
          event_name: triggerData.eventName,
          event_date_iso: triggerData.eventDateIso,
          event_location: triggerData.eventLocation,
          event_ics_url: triggerData.eventIcsUrl,
        };
        Object.entries(map).forEach(([name, value]) => {
          const field = this.form.querySelector(`[name="${name}"]`);
          if (field) field.value = value || '';
        });
      }

      populateContextEverywhere(triggerData) {
        const dateDisplay = this.formatDateForDisplay(triggerData.eventDateIso);
        this.querySelectorAll('[data-pd-event-name]').forEach((el) => {
          el.textContent = triggerData.eventName || '';
        });
        this.querySelectorAll('[data-pd-event-date]').forEach((el) => {
          el.textContent = dateDisplay;
        });
        this.querySelectorAll('[data-pd-event-location]').forEach((el) => {
          el.textContent = triggerData.eventLocation || '';
        });
      }

      formatDateForDisplay(isoString) {
        if (!isoString) return '';
        const date = new Date(isoString);
        if (isNaN(date.getTime())) return '';

        // Render in the EVENT's timezone (extracted from the ISO offset),
        // not the browser's local timezone, so a Vietnam viewer sees the
        // Atlanta event time as 9 AM EDT, not 8 PM ICT.
        // Trick: shift the absolute instant by the offset, then format in UTC.
        const offsetMatch = isoString.match(/([+-]\d{2}:\d{2}|Z)$/);
        let displayDate = date;
        if (offsetMatch && offsetMatch[1] !== 'Z') {
          const sign = offsetMatch[1][0] === '-' ? -1 : 1;
          const [hours, minutes] = offsetMatch[1].slice(1).split(':').map(Number);
          const offsetMs = sign * (hours * 60 + minutes) * 60 * 1000;
          displayDate = new Date(date.getTime() + offsetMs);
        }

        try {
          return displayDate.toLocaleString('en-AU', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            timeZone: 'UTC',
          });
        } catch (err) {
          return isoString;
        }
      }

      /**
       * Format event date for the EMAIL display (en-US + uppercase AM/PM + UTC offset).
       * The customer's location is ignored — output is always in the event's own timezone.
       *
       * Example: "2026-06-12T09:00:00-04:00" → "Friday, June 12, 2026 — 9:00 AM (UTC-04:00)"
       *
       * Sent to Klaviyo as event.event_date_display and rendered verbatim in both
       * confirmation and reminder emails. See FOI-325 Decision 3 in the client brief.
       */
      formatEventDateDisplay(isoString) {
        if (!isoString) return '';
        const offsetMatch = isoString.match(/([+-]\d{2}:\d{2}|Z)$/);
        if (!offsetMatch) return '';
        const offset = offsetMatch[1] === 'Z' ? '+00:00' : offsetMatch[1];

        // Parse the local time components straight out of the ISO string —
        // no timezone conversion. The ISO's date+time portion already IS the
        // event-local clock time.
        const localPart = isoString.slice(0, 19); // "2026-06-12T09:00:00"
        const date = new Date(`${localPart}Z`); // force UTC parse to read components below
        if (isNaN(date.getTime())) return '';

        const weekday = date.toLocaleString('en-US', { weekday: 'long', timeZone: 'UTC' });
        const month = date.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
        const day = date.getUTCDate();
        const year = date.getUTCFullYear();
        let hour = date.getUTCHours();
        const minute = date.getUTCMinutes().toString().padStart(2, '0');
        const ampm = hour >= 12 ? 'PM' : 'AM';
        hour = hour % 12 || 12;

        return `${weekday}, ${month} ${day}, ${year} — ${hour}:${minute} ${ampm} (UTC${offset})`;
      }

      /**
       * Build all-day calendar dates from the event's wall-clock date portion
       * ("YYYY-MM-DD"), with no timezone conversion. Returns both dashed
       * (Outlook startdt/enddt) and compact (Google dates=) forms. End is
       * start + 1 day because all-day end dates are exclusive.
       */
      buildCalendarDates(isoString) {
        const empty = { startIso: '', endIso: '', startCompact: '', endCompact: '' };
        const datePart = (isoString || '').slice(0, 10); // "YYYY-MM-DD"
        if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return empty;

        const [y, m, d] = datePart.split('-').map(Number);
        const next = new Date(Date.UTC(y, m - 1, d + 1)); // next calendar day, UTC-anchored
        if (isNaN(next.getTime())) return empty;

        const pad = (n) => String(n).padStart(2, '0');
        const endIso = `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`;
        return {
          startIso: datePart,
          endIso,
          startCompact: datePart.replace(/-/g, ''),
          endCompact: endIso.replace(/-/g, ''),
        };
      }

      setState(state) {
        if (!this.detailsContainer) return;
        this.detailsContainer.setAttribute('data-state', state);

        if (this.submitButton) {
          const submitting = state === 'submitting';
          this.submitButton.setAttribute('aria-disabled', submitting ? 'true' : 'false');
          if (this.submitSpinner) {
            if (submitting) this.submitSpinner.removeAttribute('hidden');
            else this.submitSpinner.setAttribute('hidden', '');
          }
        }
      }

      async onSubmit(event) {
        event.preventDefault();

        const honeypotField = this.form.querySelector('[name="website"]');
        if (honeypotField && honeypotField.value.trim() !== '') {
          this.setState('success');
          this.markSessionRegistered(this.form.querySelector('[name="event_id"]').value);
          return;
        }

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const now = Date.now();
        this.submissionTimestamps = this.submissionTimestamps.filter((t) => now - t < 60000);
        if (this.submissionTimestamps.length >= this.maxSubmissionsPer60s) {
          this.showError('Too many attempts — please wait a moment and try again.');
          return;
        }

        const email = this.form.querySelector('[name="email"]').value.trim();
        const eventDateIso = this.form.querySelector('[name="event_date_iso"]').value;
        const eventId = this.form.querySelector('[name="event_id"]').value;

        if (!email || !this.isValidEmail(email)) {
          this.showError('Please enter a valid email address.');
          return;
        }
        if (!eventDateIso || isNaN(new Date(eventDateIso).getTime())) {
          this.showError("This event is missing a valid date — please contact support.");
          return;
        }
        if (!this.companyId || !this.listId) {
          this.showError('The registration form is not configured. Please contact support.');
          return;
        }

        this.hideError();
        this.setState('submitting');
        this.submissionTimestamps.push(now);

        const activityInterest = Array.from(
          this.form.querySelectorAll('[name="activity_interest"]:checked')
        ).map((el) => el.value);

        const productInterest = Array.from(
          this.form.querySelectorAll('[name="product_interest"]:checked')
        ).map((el) => el.value);

        // All-day calendar dates derived from the event's WALL-CLOCK date — the
        // first 10 chars of the ISO ("YYYY-MM-DD") — with NO timezone conversion.
        // Klaviyo's date filter would otherwise UTC-shift a morning +offset time
        // back to the previous day, putting the all-day entry one day early.
        // End = start + 1 day (Google/Outlook treat the all-day end as exclusive).
        const calendar = this.buildCalendarDates(eventDateIso);

        const eventProperties = {
          event_id: eventId,
          event_name: this.form.querySelector('[name="event_name"]').value,
          event_date_iso: eventDateIso,
          event_date_display: this.formatEventDateDisplay(eventDateIso),
          event_location: this.form.querySelector('[name="event_location"]').value,
          event_ics_url: this.form.querySelector('[name="event_ics_url"]').value,
          event_calendar_start_iso: calendar.startIso,
          event_calendar_end_iso: calendar.endIso,
          event_calendar_start_compact: calendar.startCompact,
          event_calendar_end_compact: calendar.endCompact,
          activity_interest: activityInterest,
          product_interest: productInterest,
          block_instance_id: this.blockId,
        };

        try {
          await Promise.all([
            this.postMetric(email, eventProperties),
            this.postSubscription(email),
          ]);
          this.markSessionRegistered(eventId);
          this.setState('success');
        } catch (err) {
          console.error('[pd-event-register-form] submission failed:', err);
          let userMessage = 'Something went wrong. Please try again or email support@foildrive.com.';
          if (err && err.message === 'rate_limit') {
            userMessage = 'Klaviyo is rate-limiting requests — please try again in a moment.';
          }
          this.showError(userMessage);
          this.setState('idle');
        }
      }

      async postMetric(email, eventProperties) {
        const profileProperties = {};
        if (eventProperties.activity_interest && eventProperties.activity_interest.length) {
          profileProperties.Activity_Interests = eventProperties.activity_interest;
        }
        if (eventProperties.product_interest && eventProperties.product_interest.length) {
          profileProperties.Product_Interests = eventProperties.product_interest;
        }

        const body = {
          data: {
            type: 'event',
            attributes: {
              properties: eventProperties,
              metric: {
                data: {
                  type: 'metric',
                  attributes: { name: this.metricName },
                },
              },
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                    properties: profileProperties,
                  },
                },
              },
            },
          },
        };

        const response = await fetch(
          `${this.klaviyoBaseUrl}/events/?company_id=${encodeURIComponent(this.companyId)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              revision: this.klaviyoRevision,
            },
            body: JSON.stringify(body),
          }
        );

        if (response.status === 429) throw new Error('rate_limit');
        if (!response.ok) {
          const text = await response.text();
          throw new Error(`metric_failed:${response.status}:${text}`);
        }
        return response;
      }

      async postSubscription(email) {
        const body = {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Event Registration',
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email },
                },
              },
            },
            relationships: {
              list: { data: { type: 'list', id: this.listId } },
            },
          },
        };

        const response = await fetch(
          `${this.klaviyoBaseUrl}/subscriptions/?company_id=${encodeURIComponent(this.companyId)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              revision: this.klaviyoRevision,
            },
            body: JSON.stringify(body),
          }
        );

        if (response.status === 429) throw new Error('rate_limit');
        if (!response.ok) {
          const text = await response.text();
          throw new Error(`subscription_failed:${response.status}:${text}`);
        }
        return response;
      }

      sessionAlreadyRegistered(eventId) {
        if (!eventId) return false;
        try {
          const stored = JSON.parse(sessionStorage.getItem(this.sessionStorageKey) || '[]');
          return Array.isArray(stored) && stored.includes(eventId);
        } catch (err) {
          return false;
        }
      }

      markSessionRegistered(eventId) {
        if (!eventId) return;
        try {
          const stored = JSON.parse(sessionStorage.getItem(this.sessionStorageKey) || '[]');
          const arr = Array.isArray(stored) ? stored : [];
          if (!arr.includes(eventId)) {
            arr.push(eventId);
            sessionStorage.setItem(this.sessionStorageKey, JSON.stringify(arr));
          }
        } catch (err) {
          // sessionStorage may be unavailable (private mode); fail silent
        }
      }

      isValidEmail(email) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      }

      showError(message) {
        if (!this.errorWrapper || !this.errorMessage) return;
        this.errorMessage.textContent = message;
        this.errorWrapper.removeAttribute('hidden');
      }

      hideError() {
        if (!this.errorWrapper || !this.errorMessage) return;
        this.errorMessage.textContent = '';
        this.errorWrapper.setAttribute('hidden', '');
      }
    }
  );
}
